import { useMemo } from 'react';

import { selectIsOldAccount } from 'modules/personalAccount/actions/fetchIsOldAccount';
import { selectTeamAccountsLoading } from 'modules/teamAccount/actions/fetchTeamAccounts';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';

import { ISelectSelectedAccountJWTParams } from '../store/types';
import {
  selectAccountAddress,
  selectAccountName,
  selectAccountPermissions,
  selectAccountPermissionsInitialized,
  selectAccountRole,
  selectAccounts,
  selectIsAccountInitialized,
  selectIsAccountInitializing,
  selectIsAccountPremissionDialogHidden,
  selectIsAdmin,
  selectIsDev,
  selectIsFinance,
  selectIsOwner,
  selectIsPersonalAccount,
  selectIsTeamAccount,
  selectIsTeamAccountExist,
  selectIsTeamAccountInitialized,
  selectIsWorkerAuthorized,
  selectIsWorkerAuthorizing,
  selectTeamAccountAddress,
  selectUserEndpointToken,
  selectWorkerToken,
} from '../store/selectors';

export const useAccount = () => {
  const { authAddress } = useAuth();

  const accountAddress = useAppSelector(selectAccountAddress);
  const accountName = useAppSelector(selectAccountName);
  const accounts = useAppSelector(selectAccounts);
  const accountsLoading = useAppSelector(selectTeamAccountsLoading);
  const group = useAppSelector(selectTeamAccountAddress);
  const isAccountPremissionDialogHidden = useAppSelector(
    selectIsAccountPremissionDialogHidden,
  );
  const isAdmin = useAppSelector(selectIsAdmin);
  const isDev = useAppSelector(selectIsDev);
  const isFinance = useAppSelector(selectIsFinance);
  const isTeamAccount = useAppSelector(selectIsTeamAccount);
  const isTeamAccountExist = useAppSelector(selectIsTeamAccountExist);
  const isTeamAccountInitialized = useAppSelector(
    selectIsTeamAccountInitialized,
  );
  const isOwner = useAppSelector(selectIsOwner);
  const isPersonalAccount = useAppSelector(selectIsPersonalAccount);
  const isWorkerAuthorized = useAppSelector(selectIsWorkerAuthorized);
  const permissions = useAppSelector(selectAccountPermissions);
  const permissionsInitialized = useAppSelector(
    selectAccountPermissionsInitialized,
  );
  const role = useAppSelector(selectAccountRole);
  const userEndpointToken = useAppSelector(selectUserEndpointToken);
  const workerToken = useAppSelector(selectWorkerToken);

  const accountJWTParams: ISelectSelectedAccountJWTParams = useMemo(
    () => ({
      authAddress,
      group,
    }),
    [group, authAddress],
  );

  const isAccountInitializing = useAppSelector(state =>
    selectIsAccountInitializing(state, accountJWTParams),
  );
  const isWorkerAuthorizing = useAppSelector(state =>
    selectIsWorkerAuthorizing(state, accountJWTParams),
  );

  const isAccountInitialized = useAppSelector(selectIsAccountInitialized);

  const isOldAccount = useAppSelector(state =>
    selectIsOldAccount(state, { account: authAddress! }),
  );

  return {
    accountAddress,
    accountName,
    accounts,
    accountsLoading,
    group,
    isAccountInitialized,
    isAccountInitializing,
    isAccountPremissionDialogHidden,
    isAdmin,
    isDev,
    isFinance,
    isOldAccount,
    isOwner,
    isPersonalAccount,
    isTeamAccount,
    isTeamAccountExist,
    isTeamAccountInitialized,
    isWorkerAuthorized,
    isWorkerAuthorizing,
    permissions,
    permissionsInitialized,
    role,
    userEndpointToken,
    workerToken,
  };
};
